import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/plain.css";

import PasswordVisibilityIcon from "../components/PasswordVisibilityIcon";
import { lookupReferralSponsor, registerUser, resendEmailOtp, verifyEmail } from "../services/authService";

const OTP_RESEND_COOLDOWN_SECONDS = 300;
const SPONSOR_LOOKUP_DEBOUNCE_MS = 400;

function extractCampaignAccount({ params, searchParams, pathname }) {
  const fromParam = String(params.referralAccount || params.campaignSegment || "").trim();
  const fromSegment = /^campaign-(\d+)$/i.exec(fromParam);
  if (fromSegment) return fromSegment[1];
  if (/^\d+$/.test(fromParam)) return fromParam;

  const pathMatch = /^\/campaign-(\d+)\/?$/i.exec(String(pathname || ""));
  if (pathMatch) return pathMatch[1];

  return (searchParams.get("camp") || searchParams.get("ref") || "").trim();
}

export default function RegisterPage() {
  const navigate = useNavigate();
  const params = useParams();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const campaignRef = useMemo(
    () => extractCampaignAccount({ params, searchParams, pathname }),
    [params, searchParams, pathname]
  );
  const [step, setStep] = useState("SIGNUP");
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    country_code: "+880",
    mobile_number: "",
    referral_code_used: campaignRef,
    password: "",
    confirm_password: "",
    agree_terms: false,
  });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [sponsorPreview, setSponsorPreview] = useState(null);
  const [sponsorLookupState, setSponsorLookupState] = useState("idle");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const sponsorLocked = Boolean(campaignRef);
  const sponsorFieldBlocksSubmit =
    !sponsorLocked &&
    Boolean(form.referral_code_used.trim()) &&
    (sponsorLookupState === "loading" ||
      sponsorLookupState === "not_found" ||
      sponsorLookupState === "error");

  useEffect(() => {
    if (!campaignRef) return;
    setForm((f) => ({ ...f, referral_code_used: campaignRef }));
  }, [campaignRef]);

  useEffect(() => {
    const prefillEmail = (searchParams.get("email") || "").trim().toLowerCase();
    if (!prefillEmail) return;
    setForm((f) => (f.email ? f : { ...f, email: prefillEmail }));
  }, [searchParams]);

  useEffect(() => {
    if (sponsorLocked) {
      setSponsorPreview(null);
      setSponsorLookupState("idle");
      return;
    }
    const raw = form.referral_code_used.trim();
    if (!raw) {
      setSponsorPreview(null);
      setSponsorLookupState("idle");
      return;
    }

    const ac = new AbortController();
    let cancelled = false;
    setSponsorLookupState("loading");
    setSponsorPreview(null);

    const timer = setTimeout(async () => {
      try {
        const { data } = await lookupReferralSponsor(raw, { signal: ac.signal });
        if (cancelled) return;
        setSponsorPreview(data);
        setSponsorLookupState("ok");
      } catch (e) {
        if (cancelled || ac.signal.aborted || e.name === "CanceledError" || e.code === "ERR_CANCELED") {
          return;
        }
        setSponsorPreview(null);
        if (e.response?.status === 404) {
          setSponsorLookupState("not_found");
        } else {
          setSponsorLookupState("error");
        }
      }
    }, SPONSOR_LOOKUP_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      ac.abort();
      clearTimeout(timer);
    };
  }, [form.referral_code_used, sponsorLocked]);

  const passwordChecks = {
    minLength: form.password.length >= 8,
    hasUpper: /[A-Z]/.test(form.password),
    hasLower: /[a-z]/.test(form.password),
    hasNumber: /[0-9]/.test(form.password),
    hasSpecial: /[^A-Za-z0-9]/.test(form.password),
  };
  const formatCountdown = (totalSeconds) => {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  };

  const validate = () => {
    if (!form.full_name.trim()) {
      return "Full name is required.";
    }
    if (!/^\S+@\S+\.\S+$/.test(form.email)) {
      return "Enter a valid email address.";
    }
    if (!/^\+\d{1,4}$/.test(form.country_code.trim())) {
      return "Enter a valid country code (example: +880).";
    }
    if (!/^\d{6,15}$/.test(form.mobile_number.trim())) {
      return "Enter a valid mobile number (digits only).";
    }
    if (!passwordChecks.minLength) {
      return "Password must be at least 8 characters.";
    }
    if (!passwordChecks.hasUpper || !passwordChecks.hasLower || !passwordChecks.hasNumber || !passwordChecks.hasSpecial) {
      return "Password must include uppercase, lowercase, number, and special character.";
    }
    if (form.password !== form.confirm_password) {
      return "Password and confirm password do not match.";
    }
    const refRaw = form.referral_code_used.trim();
    if (!sponsorLocked && refRaw) {
      if (sponsorLookupState === "loading") {
        return "Wait for sponsor confirmation to finish.";
      }
      if (sponsorLookupState !== "ok" || !sponsorPreview?.sponsorFullName) {
        return "Enter a valid sponsor account number or referral code, or clear the field.";
      }
    }
    if (!form.agree_terms) {
      return "You must agree to Terms and Privacy Policy.";
    }
    return "";
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    setLoading(true);
    try {
      const referralIdentifier = form.referral_code_used.trim();
      const payload = {
        full_name: form.full_name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: `${form.country_code.trim()}${form.mobile_number.trim()}`,
        role: "INVESTOR",
        password: form.password,
      };
      if (referralIdentifier) {
        payload.referral_code_used = referralIdentifier;
      }
      const { data } = await registerUser(payload);
      setStep("VERIFY_OTP");
      setSuccess(
        data?.detail ||
          "We emailed you a 6-digit code. Your account is only created after you verify — no profile is saved until then."
      );
      setOtp("");
      setResendCooldown(OTP_RESEND_COOLDOWN_SECONDS);
    } catch (err) {
      const payload = err.response?.data;
      const msg = Array.isArray(payload)
        ? payload[0]
        : payload?.detail || payload?.email?.[0] || payload?.phone?.[0] || "Registration failed.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const onVerifyOtp = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    if (!/^\d{6}$/.test(otp)) {
      setError("Enter a valid 6-digit OTP.");
      return;
    }
    setOtpLoading(true);
    try {
      const { data } = await verifyEmail({ email: form.email.trim().toLowerCase(), otp });
      let msg =
        data?.detail || "Registration complete. Your email is verified and your account is ready. Redirecting to login…";
      if (data?.referralLink) {
        msg += ` Your referral link: ${data.referralLink}`;
      } else if (data?.accountNumber) {
        msg += ` Your investor account number: ${data.accountNumber}.`;
      }
      setSuccess(msg);
      setTimeout(() => navigate("/login"), 2200);
    } catch (err) {
      setError(err.response?.data?.detail || err.response?.data?.non_field_errors?.[0] || "OTP verification failed.");
    } finally {
      setOtpLoading(false);
    }
  };

  const onResendOtp = async () => {
    if (resendLoading || resendCooldown > 0) {
      return;
    }
    setError("");
    setSuccess("");
    setResendLoading(true);
    try {
      await resendEmailOtp({ email: form.email.trim().toLowerCase() });
      setSuccess("A new OTP has been sent to your email.");
      setResendCooldown(OTP_RESEND_COOLDOWN_SECONDS);
    } catch (err) {
      const detail = err.response?.data?.detail || err.response?.data?.non_field_errors?.[0] || "Could not resend OTP.";
      const waitMatch = detail.match(/Please wait\s+(\d+)s/i);
      if (waitMatch?.[1]) {
        setResendCooldown(Number(waitMatch[1]));
      }
      setError(detail);
    } finally {
      setResendLoading(false);
    }
  };

  useEffect(() => {
    if (step !== "VERIFY_OTP" || resendCooldown <= 0) {
      return undefined;
    }
    const timer = setInterval(() => {
      setResendCooldown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [step, resendCooldown]);

  return (
    <div className="auth-wrap">
      {step === "SIGNUP" ? (
        <form className="card auth-card stack" onSubmit={onSubmit} style={{ position: "relative" }}>
          <button
            type="button"
            className="close-btn"
            onClick={() => navigate("/")}
            aria-label="Close sign up"
          >
            ×
          </button>
          <h2>Sign Up</h2>
          <p style={{ margin: 0, color: "var(--muted)" }}>Wallet, projects, investments.</p>
          {error && <p className="msg msg-error">{error}</p>}
          {success && <p className="msg msg-success">{success}</p>}
          <input
            placeholder="Full Name"
            value={form.full_name}
            onChange={(e) => setForm({ ...form, full_name: e.target.value })}
            required
          />
          <input
            type="email"
            placeholder="Email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            required
          />
          <div>
            <PhoneInput
              country="bd"
              value={`${form.country_code.replace("+", "")}${form.mobile_number}`}
              onChange={(value, country) => {
                const dialCode = country?.dialCode || "";
                const nationalNumber = value.startsWith(dialCode) ? value.slice(dialCode.length) : "";
                setForm({
                  ...form,
                  country_code: `+${dialCode}`,
                  mobile_number: nationalNumber,
                });
              }}
              enableSearch
              disableSearchIcon
              countryCodeEditable={false}
              inputProps={{
                name: "mobile",
                required: true,
              }}
              containerClass="phone-input-container"
              inputClass="phone-input-field"
              buttonClass="phone-input-button"
              dropdownClass="phone-input-dropdown"
            />
          </div>
          <div className="password-field">
            <input
              type={showPassword ? "text" : "password"}
              placeholder="Password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              autoComplete="new-password"
              required
            />
            <button
              type="button"
              className="password-toggle"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? "Hide password" : "Show password"}
              aria-pressed={showPassword}
            >
              <PasswordVisibilityIcon visible={showPassword} />
            </button>
          </div>
          <div className="password-rules">
            <div className={passwordChecks.minLength ? "password-rule met" : "password-rule"}>
              <span>{passwordChecks.minLength ? "✓" : "○"}</span>
              <span>At least 8 characters</span>
            </div>
            <div className={passwordChecks.hasUpper ? "password-rule met" : "password-rule"}>
              <span>{passwordChecks.hasUpper ? "✓" : "○"}</span>
              <span>At least one uppercase letter (A-Z)</span>
            </div>
            <div className={passwordChecks.hasLower ? "password-rule met" : "password-rule"}>
              <span>{passwordChecks.hasLower ? "✓" : "○"}</span>
              <span>At least one lowercase letter (a-z)</span>
            </div>
            <div className={passwordChecks.hasNumber ? "password-rule met" : "password-rule"}>
              <span>{passwordChecks.hasNumber ? "✓" : "○"}</span>
              <span>At least one number (0-9)</span>
            </div>
            <div className={passwordChecks.hasSpecial ? "password-rule met" : "password-rule"}>
              <span>{passwordChecks.hasSpecial ? "✓" : "○"}</span>
              <span>At least one special character</span>
            </div>
          </div>
          <div className="password-field">
            <input
              type={showConfirmPassword ? "text" : "password"}
              placeholder="Confirm Password"
              value={form.confirm_password}
              onChange={(e) => setForm({ ...form, confirm_password: e.target.value })}
              autoComplete="new-password"
              required
            />
            <button
              type="button"
              className="password-toggle"
              onClick={() => setShowConfirmPassword((v) => !v)}
              aria-label={showConfirmPassword ? "Hide confirm password" : "Show confirm password"}
              aria-pressed={showConfirmPassword}
            >
              <PasswordVisibilityIcon visible={showConfirmPassword} />
            </button>
          </div>
          <label style={{ display: "block", margin: 0, fontSize: 14, fontWeight: 600 }}>
            Sponsor account number <span style={{ fontWeight: 400, color: "var(--muted)" }}>(optional)</span>
          </label>
          <input
            placeholder="Referral code or account number"
            value={form.referral_code_used}
            onChange={(e) => setForm({ ...form, referral_code_used: e.target.value })}
            disabled={sponsorLocked}
            autoComplete="off"
            aria-describedby="register-sponsor-hint"
          />
          {sponsorLocked && (
            <p id="register-sponsor-hint" style={{ margin: 0, color: "var(--muted)", fontSize: 13 }}>
              Link referral: <strong>{campaignRef}</strong>
            </p>
          )}
          {!sponsorLocked && (
            <>
              <p id="register-sponsor-hint" style={{ margin: 0, color: "var(--muted)", fontSize: 13 }}>
                Optional sponsor: account number or code (confirmed before submit).
              </p>
              <div style={{ minHeight: 52 }}>
                {form.referral_code_used.trim() && sponsorLookupState === "loading" && (
                  <p style={{ margin: 0, fontSize: 13, color: "var(--muted)" }}>Looking up sponsor…</p>
                )}
                {form.referral_code_used.trim() && sponsorLookupState === "ok" && sponsorPreview?.sponsorFullName && (
                  <div
                    role="status"
                    style={{
                      margin: 0,
                      padding: "10px 12px",
                      borderRadius: 8,
                      background: "var(--surface-2, rgba(37, 99, 235, 0.08))",
                      border: "1px solid var(--border)",
                      fontSize: 13,
                    }}
                  >
                    You are signing up under <strong>{sponsorPreview.sponsorFullName}</strong>
                    {sponsorPreview.accountNumber ? (
                      <>
                        {" "}
                        (account <strong>{sponsorPreview.accountNumber}</strong>)
                      </>
                    ) : null}
                    .
                  </div>
                )}
                {form.referral_code_used.trim() && sponsorLookupState === "not_found" && (
                  <p style={{ margin: 0, fontSize: 13, color: "var(--danger, #b91c1c)" }}>
                    Sponsor not found. Clear the field or fix the code.
                  </p>
                )}
                {form.referral_code_used.trim() && sponsorLookupState === "error" && (
                  <p style={{ margin: 0, fontSize: 13, color: "var(--danger, #b91c1c)" }}>
                    Sponsor lookup failed. Retry.
                  </p>
                )}
              </div>
            </>
          )}
          <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input
              type="checkbox"
              style={{ width: "auto" }}
              checked={form.agree_terms}
              onChange={(e) => setForm({ ...form, agree_terms: e.target.checked })}
            />
            I agree to the Terms and Privacy Policy.
          </label>
          <button type="submit" disabled={loading || sponsorFieldBlocksSubmit}>
            {loading ? "Creating..." : "Create Account"}
          </button>
          <p style={{ margin: 0 }}>
            <Link to="/login">Already have an account? Sign in</Link>
            {" · "}
            <Link to="/forgot-password">Forgot password</Link>
          </p>
          <p style={{ margin: 0, fontSize: 13 }} className="muted">
            <Link to="/">Home</Link>
          </p>
        </form>
      ) : (
        <form className="card auth-card stack" onSubmit={onVerifyOtp} style={{ position: "relative" }}>
          <button
            type="button"
            className="close-btn"
            onClick={() => navigate("/")}
            aria-label="Close sign up"
          >
            ×
          </button>
          <h2>Verify Your Email</h2>
          <p style={{ margin: 0, color: "var(--muted)" }}>
            Enter the 6-digit OTP sent to <strong>{form.email}</strong>.
          </p>
          {error && <p className="msg msg-error">{error}</p>}
          {success && <p className="msg msg-success">{success}</p>}
          <input
            placeholder="6-digit OTP"
            value={otp}
            maxLength={6}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
            required
          />
          <button type="submit" disabled={otpLoading}>{otpLoading ? "Verifying..." : "Verify Email"}</button>
          {resendCooldown <= 0 ? (
            <button type="button" className="btn-secondary" onClick={onResendOtp} disabled={resendLoading}>
              {resendLoading ? "Resending..." : "Resend OTP"}
            </button>
          ) : (
            <p style={{ margin: 0, color: "var(--muted)", fontSize: 13 }}>
              Resend OTP will be available in {formatCountdown(resendCooldown)}.
            </p>
          )}
          <p style={{ margin: 0, display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
            <button type="button" className="btn-secondary" onClick={() => setStep("SIGNUP")}>
              Back to sign up
            </button>
            <Link to="/login">Go to sign in</Link>
          </p>
          <p style={{ margin: 0, fontSize: 13 }} className="muted">
            <Link to="/">Home</Link>
          </p>
        </form>
      )}
    </div>
  );
}
